import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import styles from './style.module.scss'
import { useTranslation } from 'react-i18next'

import Btn from '../../../../UI/Btn'
import MiniHeader from '../../../../UI/MiniHeader'
import Search from '../../../../UI/Search'
import Table from '../../../../UI/Table'
import FilePreview from '../../../../UI/FilePreview'
import invoiceIcon from '../../../../assets/icons/invoices.svg'
import reportIcon from '../../../../assets/icons/reportIcon.svg'
import businessPlanIcon from '../../../../assets/icons/businessPlan.svg'
import insuranceIcon from '../../../../assets/icons/insuranceIcon.svg'
import paperParentalIcon from '../../../../assets/icons/paperParental.svg'
import { TApi, TRoutes } from '../../../../shared/const'
import http from '../../../../shared/http'
import FilterPanel from '../../../../UI/FilterPanel'
import useFilters from '../../../../UI/FilterPanel/useFilters'
import { companyField, documentTypeField } from '../../../../UI/FilterPanel/fields'
import BtnFilter from '../../../../UI/BtnFilter'
import Pagination from '../../../../UI/Pagination'
import { documentOwnerName, localizedDocumentTypeLabel } from '../../../../shared/helpers'
import useDebouncedValue from '../../../../shared/useDebouncedValue'
import useLatestOnly from '../../../../shared/useLatestOnly'

const documentTypesIcon = {
	INVOICE: invoiceIcon,
	REPORT: reportIcon,
	BUSINESS_PLAN: businessPlanIcon,
	INSURANCE: insuranceIcon,
	PARENTAL_CONFIRMATION: paperParentalIcon,
	CONSENT_DOCUMENT: businessPlanIcon,
	PARTICIPATION_DOCUMENT: businessPlanIcon,
	COMPANY_CERTIFICATE: businessPlanIcon,
	SHAREHOLDER_CERTIFICATE: businessPlanIcon,
	SHARE_TEMPLATE: businessPlanIcon,
	COURSE_CERTIFICATE: paperParentalIcon
}

function DocumentsList(props) {
	const { t } = useTranslation()
	const [documents, setDocuments] = useState([])
	const [countDocuments, setCountDocuments] = useState(0)
	const [page, setPage] = useState(0)
	const limit = 10
	// eslint-disable-next-line no-unused-vars
	const [company, setCompany] = useState({})
	const [sort, setSort] = useState({ name: 'name', direction: 'asc' })
	const [search, setSearch] = useState('')
	const [showFilter, setShowFilter] = useState(false)
	// Position der Datei in der Vorschau; -1 heisst: keine Vorschau offen.
	const [previewIndex, setPreviewIndex] = useState(-1)

	// Zur Auswahl stehen die betreuten Firmen — andere Dokumente zeigt die Liste
	// ohnehin nicht.
	const fields = useMemo(() => [documentTypeField(t), companyField(t, { scope: 'teacher' })], [t])

	const filters = useFilters({ storageKey: 'documents-teacher', fields })
	const filterQuery = JSON.stringify(filters.query)
	const debouncedSearch = useDebouncedValue(search)
	const latest = useLatestOnly()

	const handleSearch = e => {
		setPage(0)
		setSearch(e.target.value)
	}

	const handleFilterChange = (key, selected) => {
		setPage(0)
		filters.setField(key, selected)
	}

	const handleFilterReset = () => {
		setPage(0)
		filters.resetAll()
	}

	const download = name => {
		http.get(TApi.TEACHER_DOWNLOAD_DOCUMENT(name), {
			headers: {
				'Content-Type': 'application/pdf'
			},
			responseType: 'blob'
		}).then(({ data }) => {
			const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
			const link = document.createElement('a')
			link.href = url
			link.setAttribute('download', name)
			document.body.appendChild(link)
			link.click()
			link.parentNode.removeChild(link)
		})
	}

	const fetchDocuments = () => {
		const isCurrent = latest()

		http.get(TApi.TEACHER_GET_DOCUMENTS, {
			params: {
				limit,
				offset: page * limit,
				direction: sort.direction,
				order: sort.name,
				id: company.value ? company.value : null,
				search: debouncedSearch,
				filter: filterQuery
			}
		}).then(({ data }) => {
			if (!isCurrent()) return

			// Neue Liste, alte Position: Die Vorschau zeigt sonst die Datei, die
			// an dieser Stelle vorher stand.
			setPreviewIndex(-1)
			setDocuments(
				data.rows.map(e => {
					const date = new Date(e.createdAt)
					return {
						name: e.originalName,
						file: e.name,
						type: e.type,
						time:
							('0' + date.getDate()).slice(-2) +
							'/' +
							('0' + (date.getMonth() + 1)).slice(-2) +
							'/' +
							date.getFullYear(),
						company: e.Company ? e.Company.name : '',
						owner: documentOwnerName(e.Creator)
					}
				})
			)
			setCountDocuments(data.count)
		})
	}

	const handleSort = (name, direction) => {
		setSort({ name, direction })
	}

	// Geblättert wird durch die Liste, die gerade zu sehen ist — dieselbe
	// Reihenfolge, dieselbe Seite.
	const previewItems = useMemo(
		() =>
			documents.map(item => ({
				name: item.name,
				url: TApi.TEACHER_DOWNLOAD_DOCUMENT(item.file),
				file: item.file,
				meta: [localizedDocumentTypeLabel(item.type), item.company, item.owner, item.time]
					.filter(Boolean)
					.join(' · ')
			})),
		[documents]
	)

	// Gefiltert und gesucht wird ohne Knopfdruck; jede Aenderung setzt die Seite
	// zurueck, sonst stuende man auf Seite 4 einer kuerzer gewordenen Liste.
	useEffect(() => {
		fetchDocuments()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page, company, sort, debouncedSearch, filterQuery])

	// Dieselbe Reihenfolge wie in der Dokumentenliste der Verwaltung: Eine
	// Lehrkraft kann mehrere Firmen betreuen, gesucht wird also nach der Firma
	// und nicht nach der Dateinummer. Der Dateiname bleibt der Name des
	// Datensatzes (`primary`) und nimmt den Herunterladen-Knopf mit ans
	// Zeilenende.
	const columns = useMemo(
		() => [
			{
				Header: () => {
					return <span className={styles.tableTitleCompany}>{t('Company')}</span>
				},
				/*
				 * Fuenf Spalten muessen nebeneinander passen. Mit den frueheren
				 * 250 px lief die Kopfzeile „Datum" ueber drei Zeilen und das
				 * Datum schob sich unter das Dateisymbol, sobald die
				 * Zugehoerigkeit dazukam — dieselben Breiten wie in der Liste
				 * der Verwaltung.
				 */
				minWidth: 170,
				accessor: 'company',
				Cell: ({ row }) => {
					return <p className={styles.tableCompany}>{row.values.company}</p>
				}
			},
			{
				Header: t('Type'),
				minWidth: 170,
				accessor: 'type',
				Cell: ({ row }) => {
					return <p className={styles.tableType}>{localizedDocumentTypeLabel(row.original.type)}</p>
				}
			},
			/*
			 * Wem das Dokument gehoert. Leer, wo es niemandem zuzuordnen ist —
			 * Urkunden und Rechnungen gehoeren der Junior Company, nicht einer
			 * Person (siehe shared/helpers, documentOwnerName).
			 */
			{
				Header: t('Assigned to'),
				minWidth: 150,
				accessor: 'owner',
				Cell: ({ row }) => (
					<p className={styles.tableOwner}>{row.values.owner || <span aria-hidden="true">—</span>}</p>
				)
			},
			{
				Header: t('Date'),
				width: 80,
				accessor: 'time',
				Cell: ({ row }) => {
					return <p className={styles.tableDate}>{row.values.time}</p>
				}
			},
			{
				Header: t('Document name'),
				minWidth: 170,
				accessor: 'name',
				primary: true,
				Cell: ({ row }) => {
					return (
						<div className={styles.tableNameContainer}>
							<img
								className={styles.tableDocumentIcon}
								src={documentTypesIcon[row.values.type]}
								alt="icon"
							/>
							<span className={styles.tableName}>{row.values.name}</span>
							<Btn
								className={styles.tableDownload}
								theme="download"
								icon="download"
								iconSize={{ w: 24, h: 24 }}
								onClick={() => download(row.original.file)}
							/>
						</div>
					)
				}
			}
		],
		[t]
	)
	return (
		<div className={styles.root}>
			<MiniHeader className={styles.headerContainer} title={t('documents')}>
				<div className={styles.header}>
					<div className={styles.searchContainer}>
						<Search
							value={search}
							onChange={handleSearch}
							onSubmit={e => e.preventDefault()}
							placeholder={t('Search for documents...')}
						/>
						<BtnFilter
							icon="arrow"
							onClick={() => setShowFilter(!showFilter)}
							count={filters.count}
						>
							{t('Filter')} ({filters.count})
						</BtnFilter>
					</div>
				</div>
			</MiniHeader>
			<div className={styles.subHeader}>
				<Pagination
					limit={limit}
					page={page}
					label={t('documents')}
					countAll={countDocuments}
					onChange={setPage}
				/>
			</div>
			{/* Anforderung #182: Lehrkräfte suchten die Vorlage der Elternbestätigung hier — sie liegt im Profil. */}
			<p className={styles.templatesHint}>
				{t('Looking for the consent form template for your students?')}{' '}
				<Link to={TRoutes.PROFILE} className={styles.templatesHintLink}>
					{t('Templates for your students')}
				</Link>{' '}
				{t('in „My profile“.')}
			</p>
			<div className="container">
				<Table
					columns={columns}
					handleSort={handleSort}
					items={documents}
					limit={limit}
					countAll={countDocuments}
					onChange={setPage}
					page={page}
					paginationLabel={t('documents')}
					onRowClick={(item, row) => setPreviewIndex(row.index)}
					rowTitle={t('Preview')}
				/>
			</div>
			{previewIndex >= 0 && (
				<FilePreview
					items={previewItems}
					index={previewIndex}
					onIndexChange={setPreviewIndex}
					onClose={() => setPreviewIndex(-1)}
					onDownload={item => download(item.file)}
				/>
			)}
			<FilterPanel
				open={showFilter}
				onClose={() => setShowFilter(false)}
				fields={fields}
				values={filters.values}
				count={filters.count}
				onChange={handleFilterChange}
				onResetAll={handleFilterReset}
			/>
		</div>
	)
}

export default DocumentsList
